import React, { useCallback, useEffect, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Grid, MenuItem } from '@material-ui/core';
import { change, Field, formValueSelector } from 'redux-form';
import FormTextField from '../../../../../components/Form/FormTextField';
import FormSelect from '../../../../../components/Form/FormSelect';
import {
  required as validateRequired,
  validateMinBid,
  validateReservePrice,
  validateMinOffer,
  required,
  composeValidators,
  validateCounter
} from '../../../../../utils/validators';
import FormFormattedField from '../../../../../components/Form/FormFormattedField';
import { normalizeFormattedField } from '../../../../../utils';
import { AUCTION_TYPES, roles } from '../../../../../constants';
import moment from 'moment';
import { useUpdateStartingBid } from '../../../../../hooks/useUpdateStartingBid';
import { useCounterReserveBalancer } from '../../../../../hooks/useCounterReserveBalancer';
import {
  useCounterValidator,
  useReservePriceValidation,
  useStartingBidValidator
} from '../../../../../hooks/useGetValidators';

const VehicleInformation = ({
  auction_type,
  colors,
  trim,
  engines,
  transmissions,
  drive_trains,
  trims,
  reserve_price,
  alternative_offer,
  selectedVehicle
}) => {
  const vehicleDateEnd = useSelector(state => state?.vehicles?.selectedVehicle?.data?.date_end);
  const vehicleBids = useSelector(state => state?.vehicles?.selectedVehicle?.data?.bids);
  const vehicleReservedPriceMet = useSelector(state => state?.vehicles?.selectedVehicle?.data?.reserve_price_met);
  const currentTime = moment.utc();
  const isVehicleTimerEnded = vehicleDateEnd && moment.utc(vehicleDateEnd).isBefore(currentTime);
  const userRole = useSelector(state => state?.user?.user?.role);

  const dispatch = useDispatch();

  const isAlternativeAuction = auction_type && auction_type !== AUCTION_TYPES.AUTOAXESS;
  const isScAuction = auction_type === AUCTION_TYPES.SECOND_CHANCE;

  const updateStartingBid = useCallback(
    newStartingBid => dispatch(change('addVehicleForm', 'starting_bid', newStartingBid)),
    []
  );

  useUpdateStartingBid(alternative_offer, updateStartingBid, {
    skip: !isScAuction
  });

  const updateReserve = useCallback(reserve => dispatch(change('addVehicleForm', 'reserve_price', reserve)), []);
  const updateCounter = useCallback(counter => dispatch(change('addVehicleForm', 'alternative_offer', counter)), []);

  useCounterReserveBalancer(Number(reserve_price) || 0, Number(alternative_offer) || 0, updateReserve, updateCounter, {
    skip: !isAlternativeAuction
  });

  const reserveValidation = useReservePriceValidation(auction_type);
  const startingBidValidation = useStartingBidValidator(auction_type);
  const counterValidation = useCounterValidator(auction_type);

  return (
    <>
      <Grid item xs={12} sm={3}>
        <Field
          className="text-field"
          name="year"
          label="Year"
          component={FormTextField}
          type="text"
          fullWidth
          margin="normal"
          validate={validateRequired}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Field
          className="text-field"
          name="make"
          label="Make"
          component={FormTextField}
          type="text"
          fullWidth
          margin="normal"
          validate={validateRequired}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Field
          className="text-field"
          name="model"
          label="Model"
          component={FormTextField}
          type="text"
          fullWidth
          margin="normal"
          validate={validateRequired}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Field
          className="text-field"
          name="trim"
          label="Trim"
          component={FormSelect}
          type="text"
          fullWidth
          margin="normal"
          validate={validateRequired}
        >
          {trims.map((option, i) => (
            <MenuItem value={option.trim} key={i}>
              {option.trim}
            </MenuItem>
          ))}
        </Field>
      </Grid>
      {trim && (
        <>
          <Grid item xs={12} sm={6}>
            <Field
              className="text-field"
              name="engine"
              label="Engine"
              component={FormSelect}
              type="text"
              fullWidth
              margin="normal"
              validate={validateRequired}
            >
              {engines.map((option, i) => (
                <MenuItem value={option.optionName} key={i}>
                  {option.optionName}
                </MenuItem>
              ))}
            </Field>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field
              className="text-field"
              name="transmission"
              label="Transmission"
              component={FormSelect}
              type="text"
              fullWidth
              margin="normal"
              validate={validateRequired}
            >
              {transmissions.map((option, i) => (
                <MenuItem value={option.optionName} key={i}>
                  {option.optionName}
                </MenuItem>
              ))}
            </Field>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field
              className="text-field"
              name="drive_train"
              label="Drive Train"
              component={FormSelect}
              type="text"
              fullWidth
              margin="normal"
              validate={validateRequired}
            >
              {drive_trains.map((option, i) => (
                <MenuItem value={option.optionName} key={i}>
                  {option.optionName}
                </MenuItem>
              ))}
            </Field>
          </Grid>
          <Grid item sm={3} xs={12}>
            <Field
              className="text-field"
              name="ext_color"
              label="Ext. color"
              component={FormSelect}
              fullWidth
              margin="normal"
              validate={validateRequired}
            >
              {colors.map((color, i) => (
                <MenuItem value={color.optionName} key={i}>
                  {color.optionName}
                </MenuItem>
              ))}
            </Field>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Field
              className="text-field"
              name="starting_bid"
              label={isAlternativeAuction ? 'Starting Offer' : 'Starting Bid'}
              type="text"
              component={FormFormattedField}
              decimalScale={0}
              thousandSeparator
              prefix="$"
              fullWidth
              allowNegative={false}
              margin="normal"
              validate={startingBidValidation}
              normalize={normalizeFormattedField}
              disabled={vehicleBids && vehicleBids.length > 0 && userRole !== 'admin'}
            />
          </Grid>
          <Grid item xs={isAlternativeAuction ? 6 : 12} sm={isAlternativeAuction ? 3 : 6}>
            <Field
              className="text-field"
              name="reserve_price"
              label="Reserve price"
              type="text"
              component={FormFormattedField}
              decimalScale={0}
              allowNegative={false}
              prefix="$"
              thousandSeparator
              fullWidth
              normalize={normalizeFormattedField}
              margin="normal"
              disabled={(vehicleReservedPriceMet || isVehicleTimerEnded) && userRole === roles.SELLER}
              validate={reserveValidation}
            />
          </Grid>

          {isAlternativeAuction && (
            <Grid item xs={6} sm={3}>
              <Field
                className="text-field"
                name="alternative_offer"
                label="Counter"
                type="text"
                component={FormFormattedField}
                decimalScale={0}
                allowNegative={false}
                prefix="$"
                thousandSeparator
                fullWidth
                validate={counterValidation}
                normalize={normalizeFormattedField}
                margin="normal"
                disabled={selectedVehicle && selectedVehicle.in_battle}
              />
            </Grid>
          )}
        </>
      )}
    </>
  );
};

const selector = formValueSelector('addVehicleForm');

export default connect(state => ({
  trims: state.vehicles.vinInfoTrims,
  engines: state.vehicles.vinInfo.data.engines,
  transmissions: state.vehicles.vinInfo.data.transmissions,
  drive_trains: state.vehicles.vinInfo.data.drive_trains,
  colors: state.vehicles.vinInfo.data.colors,
  reserve_price: selector(state, 'reserve_price'),
  alternative_offer: selector(state, 'alternative_offer'),
  auction_type: formValueSelector('vinForm')(state, 'auction_type'),
  selectedVehicle: state.vehicles?.selectedVehicle?.data
}))(VehicleInformation);
